// Pure helpers for the NRR (Min Katt) cat lookup UI. No store or component imports.
// The `cat` argument is one entry of response.data.cats from GET /api/integrations/nrr/cats
// (shape: mapCat() in backend/lib/nrr-client.js). Design:
// docs/superpowers/specs/2026-09-10-nrr-animal-lookup-ui-design.md

export const CAT_SPECIES_ID = 2;

// Display order of the comparison table. `field` is the animal field, `nrrField` the cat field.
export const NRR_ROWS = [
    { field: 'pedigreeName', nrrField: 'fullName', labelId: 'input.pedigreeName', labelDefault: 'Pedigree name' },
    { field: 'birth', nrrField: 'dateOfBirth', labelId: 'input.birth', labelDefault: 'Birth' },
    { field: 'gender', nrrField: 'sex', labelId: 'input.gender', labelDefault: 'Gender' },
    { field: 'nrrRegistrationNumber', nrrField: 'registrationNumber', labelId: 'input.nrrRegistrationNumber', labelDefault: 'NRR registration number' },
    { field: 'ems', nrrField: 'ems', labelId: 'input.ems', labelDefault: 'EMS code' },
    { field: 'color', nrrField: 'colorName', labelId: 'input.color', labelDefault: 'Color' },
];

function str(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
}

export function nrrSexToGender(sex) {
    const value = str(sex).toUpperCase();
    if (value === 'M') {
        return 'Hann';
    }
    if (value === 'F') {
        return 'Hunn';
    }
    return '';
}

export function genderMatches(localGender, nrrSex) {
    const target = nrrSexToGender(nrrSex);
    if (!target) {
        return false;
    }
    const local = str(localGender).toLowerCase();
    return local === target.toLowerCase() || local === `kastrert ${target.toLowerCase()}`;
}

export function canLookup({ animal, featureFlags } = {}) {
    if (!animal || !featureFlags) {
        return false;
    }
    const flagOn = featureFlags.nrr === 1 || featureFlags.nrr === true;
    return flagOn && animal.speciesId === CAT_SPECIES_ID && str(animal.chipId) !== '';
}

export function isoDay(value) {
    if (!value) {
        return '';
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function compareRow(row, animal, cat) {
    const nrrRaw = cat[row.nrrField];
    if (row.field === 'gender') {
        const nrr = nrrSexToGender(nrrRaw);
        const local = str(animal && animal.gender);
        return {
            local, nrr, value: nrr, differs: nrr !== '' && !genderMatches(local, nrrRaw),
        };
    }
    if (row.field === 'birth') {
        const nrr = isoDay(nrrRaw);
        const local = isoDay(animal && animal.birth);
        return {
            local, nrr, value: nrr, differs: nrr !== '' && local !== nrr,
        };
    }
    const nrr = str(nrrRaw);
    const local = str(animal && animal[row.field]);
    return {
        local, nrr, value: nrr, differs: nrr !== '' && local !== nrr,
    };
}

export function buildRows(animal, cat) {
    return NRR_ROWS.map(row => ({
        field: row.field,
        labelId: row.labelId,
        labelDefault: row.labelDefault,
        ...compareRow(row, animal || {}, cat || {}),
    }));
}

export function changedFields(animal, cat) {
    if (!cat) {
        return {};
    }
    const result = {};
    buildRows(animal, cat).forEach(row => {
        if (row.differs) {
            result[row.field] = row.value;
        }
    });
    return result;
}
